import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Download, ArrowRight, Mail, Sparkles, Heart, BookOpen, ShoppingBag } from "lucide-react";
import { BOOK_STATUS, AMAZON_BOOK_URL, BOOK_PRICE_LABEL, isBookLive } from "../config/bookStatus";

const BRAND = {
  deep: "#042F2E",
  teal: "#068fa2",
  tealLight: "#25a1c2",
  cream: "#efeae7",
  gold: "#d4af6a",
  mint: "#5EEAD4",
};

const WORKBOOK_PDF_PATH = `${process.env.REACT_APP_BACKEND_URL}/api/workbook/download`;

export default function WorkbookSuccess() {
  const [autoStarted, setAutoStarted] = useState(false);

  useEffect(() => {
    document.title = "Your I GET TO Workbook is on the way — Mind Body Spirit HYGIENE™ Tools";
    // Trigger automatic download after a 1.2s beat so the user sees the success state first
    const t = setTimeout(() => {
      const a = document.createElement("a");
      a.href = WORKBOOK_PDF_PATH;
      a.download = "I_GET_TO_Workbook.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setAutoStarted(true);
    }, 1200);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: BRAND.cream }} data-testid="workbook-success-page">
      <Helmet>
        <title>Your I GET TO Workbook is on the way — Mind Body Spirit HYGIENE™ Tools</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* Minimal header */}
      <header className="px-6 py-5 border-b" style={{ borderColor: "rgba(4,47,46,0.08)" }}>
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-serif text-lg tracking-tight" style={{ color: BRAND.deep }} data-testid="success-back-home">
            Mind Body Spirit HYGIENE<sup className="text-xs">™</sup>
          </Link>
          <Link to="/build-your-own" className="font-sans text-xs tracking-wider uppercase" style={{ color: BRAND.teal }} data-testid="success-nav-build-your-own">
            Build Your Own
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 py-16 md:py-24 text-center">
        <div className="max-w-2xl mx-auto">
          <div
            className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6"
            style={{ background: BRAND.mint }}
          >
            <Sparkles className="w-10 h-10" style={{ color: BRAND.deep }} />
          </div>
          <p
            className="font-sans text-[10px] tracking-[0.35em] uppercase font-bold mb-4"
            style={{ color: BRAND.gold }}
          >
            ✦ Welcome home, Lovely
          </p>
          <h1
            className="font-serif tracking-tighter leading-[1.05] mb-5"
            style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)", color: BRAND.deep }}
            data-testid="success-headline"
          >
            Your I GET TO workbook is on the way.
          </h1>
          <p
            className="font-serif text-lg md:text-xl leading-relaxed mb-8"
            style={{ color: BRAND.teal }}
            data-testid="success-subhead"
          >
            Your download is starting now. Check your inbox in a few minutes — your bonus 365-day affirmation calendar is heading there too, along with your first note from me.
          </p>

          {/* Manual download backup */}
          <a
            href={WORKBOOK_PDF_PATH}
            download="I_GET_TO_Workbook.pdf"
            className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 font-sans font-bold tracking-wide transition-all hover:-translate-y-0.5 shadow-xl mb-3"
            style={{ background: BRAND.teal, color: "white" }}
            data-testid="success-download-btn"
          >
            <Download className="w-5 h-5" />
            {autoStarted ? "Download didn't start? Click here" : "Download the workbook"}
          </a>

          <p
            className="font-sans text-xs mt-4"
            style={{ color: "#5F7370" }}
            data-testid="success-microcopy"
          >
            Pro tip: print it. Highlight it. Live with it.
          </p>

          <p className="mt-6 text-sm italic" style={{ color: "#5F7370" }} data-testid="workbook-success-book-upsell">
            Want the whole method? The book this comes from is $9.99 as an instant PDF →{" "}
            <Link to="/book" className="underline underline-offset-4" style={{ color: BRAND.teal }}>
              get it here
            </Link>
          </p>
        </div>
      </section>

      {/* What happens next */}
      <section className="px-6 py-14 md:py-20" style={{ background: "white" }}>
        <div className="max-w-3xl mx-auto">
          <p
            className="font-sans text-[10px] tracking-[0.35em] uppercase font-bold mb-3 text-center"
            style={{ color: BRAND.gold }}
          >
            What happens next
          </p>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tighter text-center mb-10" style={{ color: BRAND.deep }}>
            You just opened the door.
          </h2>
          <div className="space-y-5">
            {[
              {
                icon: Mail,
                title: "Check your inbox",
                desc: "Your welcome email is landing now, with your 365-day affirmation calendar tucked inside.",
              },
              {
                icon: Heart,
                title: "Start the 7-day practice",
                desc: "Page 7 of the workbook walks you through your first week. One small reframe at a time.",
              },
              {
                icon: Sparkles,
                title: "Watch your Sundays",
                desc: "Each week for the next year, a short note arrives — the I GET TO journey. No noise. Just company.",
              },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div
                  key={i}
                  className="flex items-start gap-4 p-5 rounded-2xl"
                  style={{ background: BRAND.cream }}
                  data-testid={`success-next-${i}`}
                >
                  <div
                    className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: BRAND.teal }}
                  >
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl mb-1" style={{ color: BRAND.deep }}>
                      {item.title}
                    </h3>
                    <p className="font-sans text-sm md:text-base leading-relaxed" style={{ color: "#1E2A29" }}>
                      {item.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Book CTA — DIRECT BUY if live, soft if pre-launch */}
      <section className="px-6 py-14 md:py-20" data-testid="success-book-soft-cta">
        <div className="max-w-3xl mx-auto text-center">
          <BookOpen className="w-6 h-6 mx-auto mb-4" style={{ color: BRAND.gold }} />
          <p className="font-serif italic text-lg md:text-xl leading-relaxed mb-6" style={{ color: BRAND.deep }}>
            If the workbook lands for you, you&rsquo;ll love the book — it&rsquo;s the whole practice, in your hands.
          </p>
          {isBookLive() ? (
            <a
              href={AMAZON_BOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                if (typeof window !== "undefined" && typeof window.gtag === "function") {
                  window.gtag("event", "book_buy_click", {
                    source_page: "workbook_success",
                    item_name: "I GET TO Paperback",
                    value: 19.99,
                    currency: "USD",
                  });
                }
              }}
              className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 font-sans font-bold tracking-wide transition-all hover:-translate-y-0.5 shadow-xl"
              style={{ background: BRAND.mint, color: BRAND.deep }}
              data-testid="success-buy-book-cta"
            >
              <ShoppingBag className="w-5 h-5" />
              Order the paperback · {BOOK_PRICE_LABEL}
              <ArrowRight className="w-4 h-4" />
            </a>
          ) : (
            <Link
              to="/book"
              className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 font-sans font-medium tracking-wide transition-colors border"
              style={{ color: BRAND.deep, borderColor: "rgba(4,47,46,0.25)" }}
              data-testid="success-book-link"
            >
              Read about the book
              <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      </section>

      {/* Footer */}
    </div>
  );
}
